// URL-synced server-side table mixin.
// page, page size, search, status and sort live in the query string
// (?page=2&per_page=20&search=abc&status=verified&sort=dateLog&order=desc),
// so a refresh, a shared link or the back button returns to the same view.
const PAGE_SIZES = [10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 10;
const URL_KEYS = ["page", "per_page", "search", "status", "sort", "order"];

const first = (v) => (Array.isArray(v) ? v[0] : v);

export default {
  data() {
    return {
      serverOptions: {
        page: 1,
        rowsPerPage: DEFAULT_PAGE_SIZE,
        sortBy: null,
        sortType: null,
      },
      totalItems: 0,
      tableLoading: false,
      tableReqSeq: 0,
      lastTableSig: "",
      searchTimer: null,
    };
  },

  // runs before the component's own beforeMount/mounted, so the first request
  // already uses the values taken from the URL
  created() {
    this.restoreFromUrl();
  },

  watch: {
    // fires on page / page-size / sort changes made through the table itself
    serverOptions: {
      deep: true,
      handler() {
        this.loadTable();
      },
    },
  },

  beforeUnmount() {
    clearTimeout(this.searchTimer);
  },

  methods: {
    // ---- URL <-> state ----
    restoreFromUrl() {
      this._tablePath = this.$route ? this.$route.path : null;
      const q = (this.$route && this.$route.query) || {};

      const page = parseInt(first(q.page), 10);
      const perPage = parseInt(first(q.per_page), 10);
      const opts = { ...this.serverOptions };
      if (page > 0) opts.page = page;
      if (PAGE_SIZES.includes(perPage)) opts.rowsPerPage = perPage;

      const sort = first(q.sort);
      if (sort && this.sortFieldMap && this.sortFieldMap[sort]) {
        opts.sortBy = sort;
        opts.sortType = first(q.order) === "desc" ? "desc" : "asc";
      }
      this.serverOptions = opts;

      const search = first(q.search);
      if (search) this.searchValueTable = String(search).slice(0, 100);
      const status = first(q.status);
      if (status) this.selectFilterSort = String(status);
    },

    syncUrl(params) {
      // never write to the URL once the user has navigated to another page
      if (!this.$router || !this.$route || this.$route.path !== this._tablePath) return;

      const query = { ...this.$route.query };
      URL_KEYS.forEach((k) => delete query[k]);

      if (params.page > 1) query.page = String(params.page);
      if (params.page_size !== DEFAULT_PAGE_SIZE) query.per_page = String(params.page_size);
      if (params.search) query.search = params.search;
      if (params.status) query.status = String(params.status);
      const { sortBy, sortType } = this.serverOptions;
      if (sortBy && sortType) {
        query.sort = sortBy;
        query.order = sortType;
      }

      const current = this.$route.query;
      const same =
        Object.keys(query).length === Object.keys(current).length &&
        Object.keys(query).every((k) => String(current[k]) === query[k]);
      if (same) return;

      const nav = this.$router.replace({ query });
      if (nav && nav.catch) nav.catch(() => {});
    },

    orderingParam() {
      const { sortBy, sortType } = this.serverOptions;
      const field = sortBy && (this.sortFieldMap || {})[sortBy];
      if (!field) return null;
      return sortType === "desc" ? `-${field}` : field;
    },

    tableParams() {
      const params = {
        page: this.serverOptions.page,
        page_size: this.serverOptions.rowsPerPage,
      };
      const search = String(this.searchValueTable || "").trim();
      if (search) params.search = search;
      if (this.selectFilterSort) params.status = this.selectFilterSort;
      const ordering = this.orderingParam();
      if (ordering) params.ordering = ordering;
      return params;
    },

    // the cross in the search box: empty it and reload straight away
    clearSearch() {
      this.searchValueTable = "";
      clearTimeout(this.searchTimer);
      this.resetAndLoad();
    },

    // debounce typing so the API is not hit on every keystroke
    onSearchInput() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.resetAndLoad(), 400);
    },

    onStatusChange() {
      this.resetAndLoad();
    },

    // any new filter starts again from page 1
    resetAndLoad() {
      if (this.serverOptions.page !== 1) {
        this.serverOptions = { ...this.serverOptions, page: 1 };
      } else {
        this.loadTable();
      }
    },

    // force = true re-fetches the same page (e.g. after a status change)
    loadTable(force = false) {
      const params = this.tableParams();
      const sig = JSON.stringify(params);
      if (!force && sig === this.lastTableSig) return Promise.resolve();
      this.lastTableSig = sig;
      this.syncUrl(params);

      const seq = ++this.tableReqSeq; // ignore out-of-order responses
      this.tableLoading = true;
      return this.requestTable(params)
        .then((response) => {
          if (seq !== this.tableReqSeq) return;
          this.totalItems = response.data.count || 0;
          this.applyTable(response.data);
        })
        .catch((error) => {
          if (seq !== this.tableReqSeq) return;
          // page no longer exists (e.g. rows were removed / bad ?page=): go back to page 1
          if (
            error.response &&
            error.response.status === 404 &&
            this.serverOptions.page > 1
          ) {
            this.lastTableSig = "";
            this.serverOptions = { ...this.serverOptions, page: 1 };
            return;
          }
          // allow the same page to be retried after a failure
          this.lastTableSig = "";
          console.error(error);
        })
        .finally(() => {
          if (seq === this.tableReqSeq) this.tableLoading = false;
        });
    },
  },
};